import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useStaticQuery, graphql, Script, HeadProps } from 'gatsby';
import config from '../../config';
import { AcornProduct } from '../generated/types-and-hooks';
import _ from 'lodash';
import { CollectionPageProductsLD, ProductLD } from '../utils/jsonld';
import { ProductTags } from '../utils/metaTags';

const ENV_CONFIG = config.get();

type HeadInput = HeadProps & {
  includeAcceptJS?: boolean;
  title?: string;
  description?: string;
  children?: any;
  product?: AcornProduct;
  products?: AcornProduct[];
};

const useSiteMetadata = () => {
  const data = useStaticQuery(graphql`
    query SiteMetadata {
      site {
        siteMetadata {
          title
          description
          siteUrl
          social {
            twitter
          }
        }
      }
    }
  `);
  return data.site.siteMetadata;
};

const Head = (args: HeadInput) => {
  const { children, includeAcceptJS } = args;
  const siteMetadata = useSiteMetadata();

  const siteUrl = siteMetadata.siteUrl;
  const title = args.title || siteMetadata.title;
  const description = args.description || siteMetadata.description;
  const acceptJsRendered = includeAcceptJS ? (
    <Script
      id="acceptjs"
      src={ENV_CONFIG.authorizeAcceptJsUrl}
      key="acceptjs"
    />
  ) : undefined;

  let productTagsRendered;
  if (args.product) {
    productTagsRendered = (
      <Fragment key="product-jsonld">
        <ProductLD siteUrl={siteUrl} product={args.product} />
        <ProductTags siteUrl={siteUrl} product={args.product} />
      </Fragment>
    );
  }
  if (args.products) {
    productTagsRendered = (
      <Fragment key="collection-products-jsonld">
        <CollectionPageProductsLD
          siteUrl={siteUrl}
          products={args.products}
          title={title}
        />
      </Fragment>
    );
  }
  return (
    <>
      <title>{title}</title>
      <meta name="robots" content="all" />
      {acceptJsRendered}
      {children}
      {productTagsRendered}
      <Script
        key="gtag"
        id="gtag"
        src={`https://www.googletagmanager.com/gtag/js?id=${ENV_CONFIG.googleTag}`}
      />
      <Script
        key="gtag-init"
        id="gtag:init"
      >{`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
  
gtag('config', '${ENV_CONFIG.googleTag}');`}</Script>
    </>
  );
};

Head.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string,
  includeAcceptJS: PropTypes.bool,
  children: PropTypes.node,
  pathname: PropTypes.string,
};

export default Head;
